import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material'
import BigDecimal from 'js-big-decimal'
import React from 'react'
import useCustomerRevenueSummary from '../../hooks/api/useCustomerRevenueSummary'
import Tooltip from '../../common/Tooltip'

const getRounded = (num) => {
  if (!num) return '0'

  return Number(num).toFixed(2)
}

const getAllRoundedNumbers = (summary) => {
  const newVal = {}

  Object.keys(summary).forEach((key) => {
    newVal[key] = getRounded(summary[key])
  })

  return newVal
}

const CustomerRevenueSummary = ({
  customer,
  updateCounter,
  expandedAccordions,
  setExpandedAccordions,
}) => {
  const expanded = (key) => expandedAccordions?.[key]

  const setExpanded = (key, val) =>
    setExpandedAccordions((prev) => ({ ...prev, [key]: val }))

  const { customerRevenueSummary } = useCustomerRevenueSummary(customer.id, [
    updateCounter,
  ])

  const {
    currentAnnualRevenue,
    currentMonthlyRevenue,
    currentQuarterlyRevenue,
    currentSemiAnnualRevenue,
    potentialAnnualRevenue,
    potentialMonthlyRevenue,
    potentialQuarterlyRevenue,
    potentialSemiAnnualRevenue,
    ...summary
  } = customerRevenueSummary ?? {}

  const {
    pastAnnualRevenue,
    pastMonthlyRevenue,
    pastOneTimeRevenue,
    pastQuarterlyRevenue,
    pastSemiAnnualRevenue,
    totalPastRevenue,
    potentialOneTimeRevenue,
  } = getAllRoundedNumbers(summary)

  // lame
  const bigDecimal12 = new BigDecimal(12)
  const bigDecimal6 = new BigDecimal(6)
  const bigDecimal3 = new BigDecimal(3)
  //

  const estimatedCurrentMonthlyRevenue = new BigDecimal(currentMonthlyRevenue)
    .add(new BigDecimal(currentAnnualRevenue).divide(bigDecimal12))
    .add(new BigDecimal(currentSemiAnnualRevenue).divide(bigDecimal6))
    .add(new BigDecimal(currentQuarterlyRevenue).divide(bigDecimal3))
    .round(2)
    .getPrettyValue(3, ',')

  const rawEstimatedPotentialMonthlyRevenue = new BigDecimal(
    potentialMonthlyRevenue
  )
    .add(new BigDecimal(potentialAnnualRevenue).divide(bigDecimal12))
    .add(new BigDecimal(potentialSemiAnnualRevenue).divide(bigDecimal6))
    .add(new BigDecimal(potentialQuarterlyRevenue).divide(bigDecimal3))

  const estimatedPotentialMonthlyRevenue = rawEstimatedPotentialMonthlyRevenue
    .round(2)
    .getPrettyValue(3, ',')

  const totalPotentialRevenue = rawEstimatedPotentialMonthlyRevenue
    .add(new BigDecimal(summary.potentialOneTimeRevenue))
    .round(2)
    .getPrettyValue(3, ',')

  return (
    <>
      <Accordion
        elevation={8}
        sx={{ width: '100%', minWidth: '340px', maxWidth: '1250px' }}
        expanded={expanded('current')}
        onChange={(_, isExpanded) => setExpanded('current', isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ pr: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mr: 2,
            }}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <b>Current Recurring Revenue</b>
              <Tooltip
                textProps={{ sx: { whiteSpace: 'pre-line' } }}
                infoText={
                  'In this section you can view all active recurring revenue generated by any and all deals that have ever been created for this account. If a deal has been lost or is cancelled, that revenue won’t show up here.\nNote: Certain recurring payments may not be visible here until the day of the actual "Due Date."'
                }
              />
            </Typography>
            <Box>
              <Typography>
                Total recurring (estimated monthly): $
                {estimatedCurrentMonthlyRevenue}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Typography sx={{ width: '300px' }}>
              Total recurring (estimated monthly):
            </Typography>
            ${estimatedCurrentMonthlyRevenue}
          </Box>

          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Monthly:</Typography>$
            {currentMonthlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Quarterly:</Typography>$
            {currentQuarterlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Semi Annually:</Typography>$
            {currentSemiAnnualRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Annually:</Typography>$
            {currentAnnualRevenue}
          </Box>
        </AccordionDetails>
      </Accordion>

      <Accordion
        elevation={8}
        sx={{ width: '100%', maxWidth: '1250px' }}
        expanded={expanded('potential')}
        onChange={(_, isExpanded) => setExpanded('potential', isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ pr: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              flexWrap: 'wrap',
              mr: 2,
            }}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <b>Potential Revenue</b>
              <Tooltip
                textProps={{ sx: { whiteSpace: 'pre-line' } }}
                infoText={`In this section you are able to view potential revenue in 2 ways:\n1. Pending revenue from “Current Opportunity”\n2. Deals that have been won, but due to the revenue beginning at a future start date, have not yet been realized.`}
              />
            </Typography>
            <Typography>
              Total recurring (estimated monthly): $
              {estimatedPotentialMonthlyRevenue}
            </Typography>

            <Typography>Total One Time: ${potentialOneTimeRevenue}</Typography>
            <Typography>Grand Total: ${totalPotentialRevenue}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Total one-time:</Typography>$
            {potentialOneTimeRevenue}
          </Box>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Typography sx={{ width: '300px' }}>
              Total recurring (estimated monthly):
            </Typography>
            ${estimatedPotentialMonthlyRevenue}
          </Box>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Typography sx={{ width: '300px' }}>Grand Total:</Typography>$
            {totalPotentialRevenue}
          </Box>

          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Monthly:</Typography>$
            {potentialMonthlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Quarterly:</Typography>$
            {potentialQuarterlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Semi Annually:</Typography>$
            {potentialSemiAnnualRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Annually:</Typography>$
            {potentialAnnualRevenue}
          </Box>
        </AccordionDetails>
      </Accordion>
      <Accordion
        elevation={8}
        sx={{ width: '100%', maxWidth: '1250px' }}
        expanded={expanded('historic')}
        onChange={(_, isExpanded) => setExpanded('historic', isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mr: 2,
            }}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              <b>Historic Revenue</b>
              <Tooltip
                infoText={
                  'In this section, you can track "All Acquired Revenue" for this account, both Active and Inactive, that you have ever received.'
                }
              />
            </Typography>
            <Typography>Total: ${totalPastRevenue}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Typography sx={{ width: '300px' }}>Total:</Typography>$
            {totalPastRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>One-time:</Typography>$
            {pastOneTimeRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Monthly:</Typography>$
            {pastMonthlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Quarterly:</Typography>$
            {pastQuarterlyRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Semi Annually:</Typography>$
            {pastSemiAnnualRevenue}
          </Box>
          <Box sx={{ display: 'flex' }}>
            <Typography sx={{ width: '300px' }}>Annually:</Typography>$
            {pastAnnualRevenue}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default CustomerRevenueSummary
